import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../axiosConfig';
import '../../styles/explorePageCss/bookDisplay.css';
import PDFCoverPreview from '../../components/PDFCoverPreview';
import TagsFilter, { bookMatchesSelectedTags } from '../../components/TagsFilter';

const CHIP_TONES = ['primary', 'mint', 'sky', 'lilac', 'peach'];

const FILTER_DEFS = [
  { key: 'subject', label: 'Subject', style: 'checkbox' },
  { key: 'grade', label: 'Grade', style: 'pills' },
  { key: 'bookType', label: 'Book Type', style: 'checkbox' },
  { key: 'version', label: 'Version', style: 'checkbox' },
  { key: 'language', label: 'Language', style: 'checkbox' },
  { key: 'country', label: 'Country', style: 'checkbox' },
];

const VERSION_FILTER_OPTIONS = [
  { value: 'Digital', label: 'Digital' },
  { value: 'Print', label: 'Print' },
];

const BOOKS_PER_PAGE = 20;

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items = [1];
  if (current > 3) items.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p += 1) {
    if (!items.includes(p)) items.push(p);
  }
  if (current < total - 2) items.push('…');
  if (!items.includes(total)) items.push(total);
  return items;
}

const Books = () => {
  const [books, setBooks] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [openTagSection, setOpenTagSection] = useState(true);
  const initialFilters = {
    subject: [],
    grade: [],
    bookType: [],
    version: [],
    language: [],
    country: [],
    standards: [],
    isbn: [],
    tags: [],
  };
  const [filters, setFilters] = useState(initialFilters);
  const [openFilterSections, setOpenFilterSections] = useState(() =>
    Object.fromEntries(FILTER_DEFS.map(({ key }) => [key, true]))
  );
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeBlobUrl, setIframeBlobUrl] = useState(null);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerFullscreen, setViewerFullscreen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const toggleFilterValue = (category, value) => {
    const next = String(value);
    setFilters((prev) => {
      const current = prev[category] || [];
      const exists = current.includes(next);
      return {
        ...prev,
        [category]: exists ? current.filter((v) => v !== next) : [...current, next],
      };
    });
  };

  const toggleFilterSection = (key) => {
    setOpenFilterSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getName = (list, id, key) =>
    list.find((item) => String(item[key]) === String(id))?.[`${key.replace('_id', '')}_name`] || '—';
  const getGradeName = (id) =>
    grades.find((g) => String(g.grade_id) === String(id))?.grade_level || '—';

  const unique = (key) =>
    [
      ...new Set(
        books
          .map((b) => {
            switch (key) {
              case 'bookType':
                return b.book_type_title;
              case 'country':
                return b.country_name;
              case 'isbn':
                return b.isbn_code;
              default:
                return b[key];
            }
          })
          .filter((v) => v)
          .map((v) => v.toString().trim())
      ),
    ];

  const filterOptions = (cat) => {
    if (cat === 'subject') {
      return subjects.map((o) => ({ value: String(o.subject_id), label: o.subject_name }));
    }
    if (cat === 'grade') {
      return grades.map((o) => ({ value: String(o.grade_id), label: o.grade_level }));
    }
    if (cat === 'language') {
      return languages.map((o) => ({ value: String(o.language_id), label: o.language_name }));
    }
    if (cat === 'version') {
      return VERSION_FILTER_OPTIONS;
    }
    return unique(cat).map((v) => ({ value: String(v), label: v }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const [booksRes, gradesRes, subjectsRes, languagesRes, tagsRes] =
          await Promise.all([
            axios.get('/books/grouped', { headers }),
            axios.get('/grades', { headers }),
            axios.get('/subjects', { headers }),
            axios.get('/languages', { headers }),
            axios.get('/tags', { headers }).catch(() => ({ data: [] })),
          ]);

        const groupedBooks = (booksRes.data.books || booksRes.data || []).map((group) => ({
          ...group,
          grade_name: group.grade_level || group.grade_name || 'N/A',
          version_label: group.version_label || 'N/A',
          isbn_code: group.isbn_code || 'N/A',
          subject_name: group.subject_name || 'N/A',
          language_name: group.language_name || 'N/A',
          book_type_title: group.book_type_title || 'N/A',
          country_name: group.country_name || 'N/A',
        }));

        setBooks(groupedBooks);
        setFilteredBooks(groupedBooks);
        setGrades(gradesRes.data);
        setSubjects(subjectsRes.data);
        setLanguages(languagesRes.data);
        setTagOptions(
          (tagsRes.data || []).map((t) => ({ tag_id: t.tag_id, tag_name: t.tag_name }))
        );
      } catch (err) {
        console.error('Fetch error:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const filtered = books.filter((group) => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const searchMatch =
        !searchTerm ||
        (group.title?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.description?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.grade_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.subject_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.language_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.country_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.isbn_code?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        group.tags?.some((tag) => tag.tag_name.toLowerCase().includes(lowerCaseSearchTerm));

      if (!searchMatch) return false;

      const match = (list, val) => list.length === 0 || list.includes(String(val));
      const matchVersion =
        !filters.version?.length ||
        filters.version.some(
          (v) => (v === 'Digital' && group.digital) || (v === 'Print' && group.print)
        );
      return (
        match(filters.grade, group.grade_id) &&
        match(filters.subject, group.subject_id) &&
        match(filters.bookType, group.book_type_title) &&
        matchVersion &&
        match(filters.country, group.country_name) &&
        match(filters.language, group.language_id) &&
        match(filters.standards, group.standard_id) &&
        match(filters.isbn, group.isbn_code) &&
        bookMatchesSelectedTags(group, filters.tags)
      );
    });
    setFilteredBooks(filtered);
    setCurrentPage(1);
  }, [filters, books, searchTerm]);

  const closeIframe = useCallback(() => {
    setShowIframe(false);
    setIframeUrl(null);
    setIframeLoading(false);
    setViewerTitle('');
    setViewerFullscreen(false);
    if (iframeBlobUrl) {
      URL.revokeObjectURL(iframeBlobUrl);
      setIframeBlobUrl(null);
    }
  }, [iframeBlobUrl]);

  const openViewer = (url, title) => {
    setViewerTitle(title || 'Book Reader');
    setViewerFullscreen(false);
    setIframeUrl(url);
    setShowIframe(true);
    setViewMenuOpen(false);
    setPdfMenuOpen(false);
  };

  const hasUploaded = (book, flag) =>
    !!(book && (book[flag] === true || book[flag] === 1 || book[flag] === '1'));

  const openBookVersion = async (book, label) => {
    if (!book?.book_id) {
      alert(`No ${label} version is available for this book.`);
      return;
    }
    const title = `${selectedGroup?.title || book.title || 'Book Reader'} (${label})`;
    const externalUrl = (book.url || selectedGroup?.url || '').trim();
    if (externalUrl) {
      openViewer(externalUrl, title);
      return;
    }

    try {
      setIframeLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/books/${book.book_id}/stream-version`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        throw new Error('Book file not available');
      }

      if (iframeBlobUrl) URL.revokeObjectURL(iframeBlobUrl);
      const blob = new Blob([response.data], {
        type: contentType || 'application/pdf',
      });
      const objectUrl = URL.createObjectURL(blob);
      setIframeBlobUrl(objectUrl);
      openViewer(objectUrl, title);
    } catch (err) {
      console.error('Failed to open book:', err);
      alert(`Unable to open the ${label.toLowerCase()} version. No uploaded PDF or URL is available.`);
    } finally {
      setIframeLoading(false);
    }
  };

  const handleViewBookClick = () => {
    if (!selectedGroup) return;
    const digital = selectedGroup.digital;
    const print = selectedGroup.print;
    if (digital && print) {
      setViewMenuOpen(true);
      return;
    }
    if (digital) {
      openBookVersion(digital, 'Digital');
      return;
    }
    if (print) {
      openBookVersion(print, 'Print');
    }
  };

  const parseDownloadFilename = (contentDisposition, fallback) => {
    if (!contentDisposition) return fallback;
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8?.[1]) {
      try {
        return decodeURIComponent(utf8[1]);
      } catch {
        /* keep looking */
      }
    }
    const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
    if (quoted?.[1]) return quoted[1];
    const plain = /filename=([^;]+)/i.exec(contentDisposition);
    if (plain?.[1]) return plain[1].trim();
    return fallback;
  };

  const downloadOriginalFile = async (url, fallbackName) => {
    const token = localStorage.getItem('token');
    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/pdf, application/zip, application/octet-stream',
      },
      responseType: 'arraybuffer',
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      transformResponse: [(data) => data],
    });
    const contentType = String(res.headers['content-type'] || '');
    if (contentType.includes('application/json')) {
      throw new Error('Download failed');
    }
    const blob = new Blob([res.data], {
      type: contentType.split(';')[0] || 'application/octet-stream',
    });
    const filename = parseDownloadFilename(res.headers['content-disposition'], fallbackName);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  };

  const digital = selectedGroup?.digital;
  const print = selectedGroup?.print;
  const canUseDigital = !!(digital?.can_download);
  const canUsePrint = !!(print?.can_download);
  const zipBook =
    (canUseDigital && (hasUploaded(digital, 'has_zip') || digital?.zip_link) && digital) ||
    (canUsePrint && (hasUploaded(print, 'has_zip') || print?.zip_link) && print) ||
    null;

  const downloadOptions = [
    {
      key: 'digital_version',
      label: 'Digital Version (PDF)',
      available: canUseDigital && hasUploaded(digital, 'has_file'),
      run: () =>
        downloadOriginalFile(
          `/books/${digital.book_id}/download/${encodeURIComponent(digital.version_label)}`,
          `Book_${digital.book_id}_${digital.version_label}.pdf`
        ),
    },
    {
      key: 'digital_cover',
      label: 'Digital Cover (PDF)',
      available: canUseDigital && hasUploaded(digital, 'has_cover'),
      run: () =>
        downloadOriginalFile(
          `/books/${digital.book_id}/download-cover`,
          `Book_${digital.book_id}_cover.pdf`
        ),
    },
    {
      key: 'print_version',
      label: 'Print Version (PDF)',
      available: canUsePrint && hasUploaded(print, 'has_file'),
      run: () =>
        downloadOriginalFile(
          `/books/${print.book_id}/download/${encodeURIComponent(print.version_label)}`,
          `Book_${print.book_id}_${print.version_label}.pdf`
        ),
    },
    {
      key: 'print_cover',
      label: 'Print Cover (PDF)',
      available: canUsePrint && hasUploaded(print, 'has_cover'),
      run: () =>
        downloadOriginalFile(
          `/books/${print.book_id}/download-cover`,
          `Book_${print.book_id}_cover.pdf`
        ),
    },
    {
      key: 'zip_file',
      label: 'ZIP File',
      available: !!zipBook,
      run: () =>
        downloadOriginalFile(
          `/books/${zipBook.book_id}/download-zip/${encodeURIComponent(zipBook.version_label)}`,
          `Book_${zipBook.book_id}_${zipBook.version_label}.zip`
        ),
    },
  ];

  const runDownloadOption = async (option) => {
    if (!option?.available || downloadingKey) return;
    try {
      setDownloadingKey(option.key);
      await option.run();
    } catch (err) {
      alert('Download not permitted or this file is not available.');
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setIsDescriptionExpanded(false);
    setViewMenuOpen(false);
    setPdfMenuOpen(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('bookx-details-overlay')) {
      setSelectedGroup(null);
      setViewMenuOpen(false);
      setPdfMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape' || !showIframe) return;
      if (viewerFullscreen) {
        setViewerFullscreen(false);
        return;
      }
      closeIframe();
    };
    if (showIframe) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [showIframe, viewerFullscreen, closeIframe]);

  let truncatedDescription = '';
  let isLongDescription = false;
  const modalMain = selectedGroup?.digital || selectedGroup?.print || null;
  if (selectedGroup) {
    const wordLimit = 100;
    const fullDescription = selectedGroup.description || 'No description available.';
    const words = fullDescription.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean);
    isLongDescription = words.length > wordLimit;
    truncatedDescription =
      isDescriptionExpanded || !isLongDescription
        ? fullDescription
        : `${words.slice(0, wordLimit).join(' ')}…`;
  }

  const totalPages = Math.max(1, Math.ceil(filteredBooks.length / BOOKS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * BOOKS_PER_PAGE;
  const pagedBooks = filteredBooks.slice(pageStart, pageStart + BOOKS_PER_PAGE);
  const showingFrom = filteredBooks.length ? pageStart + 1 : 0;
  const showingTo = Math.min(pageStart + BOOKS_PER_PAGE, filteredBooks.length);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <div className="bookx-container">
      <div className="page-head">
        <div>
          <h1 className="page-title">Books Available</h1>
          <p className="page-desc">Browse the full shared catalogue the way a reader would.</p>
        </div>
      </div>

      <div className="layout-with-filters explore-books-layout">
        <aside>
          <div className="filter-card explore-filter-panel">
            <div className="explore-filter-head">
              <div className="explore-filter-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span>Filters</span>
              </div>
              <button type="button" className="explore-filter-clear" onClick={handleClearFilters}>
                Clear all
              </button>
            </div>

            <div className="explore-filter-body">
              {FILTER_DEFS.map(({ key, label, style }) => {
                const options = filterOptions(key);
                if (options.length === 0) return null;
                const selected = filters[key] || [];
                const isOpen = openFilterSections[key] !== false;
                const selectedCount = selected.length;

                return (
                  <section key={key} className={`explore-filter-section${isOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="explore-filter-section-toggle"
                      onClick={() => toggleFilterSection(key)}
                      aria-expanded={isOpen}
                    >
                      <span className="explore-filter-section-title">
                        {label}
                        {selectedCount > 0 ? (
                          <span className="explore-filter-count">{selectedCount}</span>
                        ) : null}
                      </span>
                      <svg
                        className="explore-filter-chevron"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {isOpen ? (
                      style === 'pills' ? (
                        <div className={`explore-filter-pills${key === 'grade' ? ' is-scrollable' : ''}`}>
                          {options.map((opt) => {
                            const active = selected.includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                className={`explore-filter-pill ${active ? 'is-active' : ''}`}
                                onClick={() => toggleFilterValue(key, opt.value)}
                                title={opt.label}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <ul className="explore-filter-checks">
                          {options.map((opt) => {
                            const checked = selected.includes(opt.value);
                            return (
                              <li key={opt.value}>
                                <label className={`explore-check ${checked ? 'is-checked' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleFilterValue(key, opt.value)}
                                  />
                                  <span className="explore-check-box" aria-hidden="true" />
                                  <span className="explore-check-label">{opt.label}</span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )
                    ) : null}
                  </section>
                );
              })}

              <section className={`explore-filter-section${openTagSection ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="explore-filter-section-toggle"
                  onClick={() => setOpenTagSection((prev) => !prev)}
                  aria-expanded={openTagSection}
                >
                  <span className="explore-filter-section-title">
                    Tags
                    {filters.tags?.length > 0 ? (
                      <span className="explore-filter-count">{filters.tags.length}</span>
                    ) : null}
                  </span>
                  <svg
                    className="explore-filter-chevron"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {openTagSection ? (
                  <TagsFilter
                    tagOptions={tagOptions}
                    selected={filters.tags || []}
                    onChange={(next) => setFilters((prev) => ({ ...prev, tags: next }))}
                  />
                ) : null}
              </section>
            </div>
          </div>
        </aside>

        <div>
          <div className="explore-toolbar">
            <input
              type="text"
              className="text-input"
              style={{ width: '100%' }}
              placeholder="Search by title, description, or tag…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <p className="explore-showing">
              Showing {showingFrom}–{showingTo} of {filteredBooks.length}{' '}
              {filteredBooks.length === 1 ? 'book' : 'books'}
            </p>
          </div>

          <div className="explore-grid">
            {pagedBooks.length === 0 ? (
              <div className="empty-note" style={{ gridColumn: '1 / -1' }}>
                No books match selected filters.
              </div>
            ) : (
              pagedBooks.map((group) => {
                const main = group.digital || group.print;
                if (!main) return null;
                const bookUrl = group.url || main?.url || null;
                if (main && !main.url && bookUrl) main.url = bookUrl;

                const title = group.title || main.title || 'Untitled';
                const gradeLabel =
                  group.grade_name && group.grade_name !== 'N/A' ? group.grade_name : null;
                const pillChips = [];
                if (group.book_type_title && group.book_type_title !== 'N/A') {
                  pillChips.push(group.book_type_title);
                }
                if (group.subject_name && group.subject_name !== 'N/A') {
                  pillChips.push(group.subject_name);
                }
                if (group.country_name && group.country_name !== 'N/A') {
                  pillChips.push(group.country_name);
                }
                if (group.language_name && group.language_name !== 'N/A') {
                  pillChips.push(group.language_name);
                }
                if (group.version_label && group.version_label !== 'N/A') {
                  pillChips.push(group.version_label);
                }
                group.tags?.slice(0, 2).forEach((tag) => {
                  if (tag?.tag_name) pillChips.push(tag.tag_name);
                });

                return (
                  <article
                    key={`${group.title}-${group.isbn_code}-${group.grade_id}-${group.version_label}`}
                    className="explore-card"
                  >
                    <div className="explore-cover">
                      <PDFCoverPreview
                        pdfUrl={`/books/${main.book_id}/stream-cover`}
                        height={300}
                        bookTitle={title}
                      />
                      {(group.digital || group.print) && (
                        <div className="explore-format-badges">
                          {group.digital ? <span className="explore-format-badge">Digital</span> : null}
                          {group.print ? <span className="explore-format-badge">Print</span> : null}
                        </div>
                      )}
                    </div>
                    <div className="explore-body">
                      <h3 className="explore-title" title={title}>
                        {title}
                      </h3>
                      <div className="explore-tags">
                        {gradeLabel ? <span className="explore-meta-plain">{gradeLabel}</span> : null}
                        {pillChips.slice(0, 4).map((chip, i) => (
                          <span key={`${chip}-${i}`} className={`chip chip-${CHIP_TONES[i % CHIP_TONES.length]}`}>
                            {chip}
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="explore-view-btn"
                        onClick={() => handleSelectGroup(group)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        View
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {filteredBooks.length > 0 && (
            <div className="pagination explore-pagination">
              <span className="page-info">
                Showing {showingFrom} to {showingTo} of {filteredBooks.length} books
              </span>
              {totalPages > 1 && (
                <>
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={safePage === 1}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  {pageItems(safePage, totalPages).map((item, idx) =>
                    item === '…' ? (
                      <button key={`e-${idx}`} type="button" className="page-btn" disabled>
                        …
                      </button>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`page-btn${item === safePage ? ' active' : ''}`}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={safePage === totalPages}
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showIframe && iframeUrl && (
        <div
          className={`explore-viewer-overlay ${viewerFullscreen ? 'is-fullscreen' : ''}`}
          onClick={(e) => {
            if (e.target.classList.contains('explore-viewer-overlay')) closeIframe();
          }}
          role="dialog"
          aria-modal="true"
          aria-label={viewerTitle}
        >
          <div className="explore-viewer-modal">
            <div className="explore-viewer-header">
              <h2 className="explore-viewer-title" title={viewerTitle}>
                {viewerTitle}
              </h2>
              <div className="explore-viewer-actions">
                <button
                  type="button"
                  className="explore-viewer-fullscreen"
                  onClick={() => setViewerFullscreen((v) => !v)}
                  aria-label={viewerFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  title={viewerFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  {viewerFullscreen ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                      <path d="M9 3H5v4M15 3h4v4M9 21H5v-4M15 21h4v-4" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                      <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  className="explore-viewer-close"
                  onClick={closeIframe}
                  aria-label="Close reader"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="explore-viewer-body">
              <iframe src={iframeUrl} className="explore-viewer-frame" title={viewerTitle} allowFullScreen />
            </div>
          </div>
        </div>
      )}

      {iframeLoading && (
        <div className="explore-viewer-overlay explore-iframe-loading">
          <div className="explore-viewer-loading-card">Opening book…</div>
        </div>
      )}

      {selectedGroup && modalMain && (
        <div
          className="bookx-details-overlay"
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
        >
          <div className="bookx-details-container">
            <div className="bookx-details-header">
              <h3 className="section-heading">Book Specifications</h3>
            </div>
            <button
              type="button"
              className="bookx-close-button"
              onClick={() => {
                setSelectedGroup(null);
                setViewMenuOpen(false);
                setPdfMenuOpen(false);
              }}
              aria-label="Close details"
            >
              ×
            </button>

            <div className="bookx-details-content">
              <div className="bookx-table-cover-row">
                <div className="bookx-details-table-container">
                  <table className="bookx-details-table">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Subject</strong>
                        </td>
                        <td>{selectedGroup.subject_name || getName(subjects, selectedGroup.subject_id, 'subject_id')}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Grade</strong>
                        </td>
                        <td>{selectedGroup.grade_name || getGradeName(selectedGroup.grade_id)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Language</strong>
                        </td>
                        <td>{selectedGroup.language_name || getName(languages, selectedGroup.language_id, 'language_id')}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Type</strong>
                        </td>
                        <td>{selectedGroup.book_type_title || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Country</strong>
                        </td>
                        <td>{selectedGroup.country_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>ISBN</strong>
                        </td>
                        <td>{selectedGroup.isbn_code || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Formats</strong>
                        </td>
                        <td>
                          {[selectedGroup.digital ? 'Digital' : null, selectedGroup.print ? 'Print' : null]
                            .filter(Boolean)
                            .join(' · ') || 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="bookx-details-image-wrapper">
                  <PDFCoverPreview
                    pdfUrl={`/books/${modalMain.book_id}/stream-cover`}
                    width={280}
                    height={360}
                    bookTitle={selectedGroup.title}
                  />
                </div>
              </div>
              <div className="bookx-details-meta">
              <h2 className="bookx-details-title">{selectedGroup.title}</h2>
              <div
                className="bookx-details-description"
                style={{ whiteSpace: 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: truncatedDescription }}
              />
              {isLongDescription && (
                <button
                  type="button"
                  className="btn-quiet"
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                >
                  {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                </button>
              )}

              <div className="bookx-details-tags">
                <strong>Tags: </strong>
                {selectedGroup.tags?.length > 0
                  ? selectedGroup.tags.map((tag) => (
                      <span key={tag.tag_id} className="chip chip-lilac">
                        {tag.tag_name}
                      </span>
                    ))
                  : 'None'}
              </div>
              </div>
            </div>

              <div className="btn-row bookx-details-buttons">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSelectedGroup(null);
                    setViewMenuOpen(false);
                    setPdfMenuOpen(false);
                  }}
                >
                  Back
                </button>
                {(selectedGroup.digital || selectedGroup.print) && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleViewBookClick}
                    disabled={iframeLoading}
                  >
                    {iframeLoading ? 'Opening…' : 'View Book'}
                  </button>
                )}
                {(canUseDigital || canUsePrint) && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setPdfMenuOpen(true)}
                  >
                    Download PDF
                  </button>
                )}
              </div>
            </div>
          </div>
      )}

      {viewMenuOpen && selectedGroup && (
        <div
          className="explore-choice-overlay"
          onClick={(e) => {
            if (e.target.classList.contains('explore-choice-overlay')) setViewMenuOpen(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Choose version to view"
        >
          <div className="explore-choice-popup">
            <div className="explore-choice-head">
              <h3>View version</h3>
              <button
                type="button"
                className="explore-choice-close"
                onClick={() => setViewMenuOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="explore-choice-copy">Choose which format you want to open.</p>
            <div className="explore-choice-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!selectedGroup.digital || iframeLoading}
                onClick={() => openBookVersion(selectedGroup.digital, 'Digital')}
              >
                Digital Version
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!selectedGroup.print || iframeLoading}
                onClick={() => openBookVersion(selectedGroup.print, 'Print')}
              >
                Print Version
              </button>
            </div>
          </div>
        </div>
      )}

      {pdfMenuOpen && selectedGroup && (
        <div
          className="explore-choice-overlay"
          onClick={(e) => {
            if (e.target.classList.contains('explore-choice-overlay')) setPdfMenuOpen(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Download files"
        >
          <div className="explore-choice-popup">
            <div className="explore-choice-head">
              <h3>Download</h3>
              <button
                type="button"
                className="explore-choice-close"
                onClick={() => setPdfMenuOpen(false)}
                aria-label="Close download options"
              >
                ×
              </button>
            </div>
            <p className="explore-choice-copy">
              Available files can be downloaded. Options that were not uploaded are marked as not available.
            </p>
            <div className="explore-choice-actions explore-download-grid">
              {downloadOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`explore-download-tile${
                    option.available ? ' is-available' : ' is-unavailable'
                  }${option.key === 'zip_file' ? ' explore-download-tile-zip' : ''}`}
                  disabled={!option.available || !!downloadingKey}
                  onClick={() => runDownloadOption(option)}
                >
                  <span className="explore-download-tile-label">{option.label}</span>
                  <span className="explore-download-tile-status">
                    {downloadingKey === option.key
                      ? 'Downloading…'
                      : option.available
                        ? 'Available'
                        : 'Not available'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Books;
